import { z } from "zod";
import {
  MAX_SELECTED_SKILLS,
  SKILLS_RESOLVE_VERSION,
  type Skill,
  type SkillDataSourceSelection,
  type SkillListResponse,
  type SkillSummary,
  type SkillsResolveRequest,
  type SkillsResolveResponse,
} from "./types";

type JsonObject = Record<string, unknown>;

export function isRecord(value: unknown): value is JsonObject {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function hasOwn(record: JsonObject, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isNullableStringArray(value: unknown): value is string[] | null {
  return value === null || isStringArray(value);
}

function hasValidOptionalString(record: JsonObject, key: string, nullable = false): boolean {
  if (!hasOwn(record, key)) return true;
  return nullable ? isNullableString(record[key]) : typeof record[key] === "string";
}

export function idsMatchSkills(
  ids: readonly string[],
  skills: readonly { id?: string }[]
): boolean {
  return ids.length === skills.length && ids.every((id, index) => id === skills[index]?.id);
}

const nonEmptyStringSchema = z.string().refine((value) => value.trim().length > 0);

const skillIdListSchema = z
  .array(nonEmptyStringSchema)
  .max(MAX_SELECTED_SKILLS)
  .refine((ids) => new Set(ids).size === ids.length);

const skillDecisionSkillSchema = z.object({
  id: nonEmptyStringSchema,
  name: nonEmptyStringSchema,
  slug: nonEmptyStringSchema,
  origin: z.enum(["personal", "organization", "finster"]),
  description: z.string(),
});

const recommendedFieldsSchema = z
  .object({
    recommended_skill_ids: skillIdListSchema,
    recommended_skills: z.array(skillDecisionSkillSchema),
  })
  .refine((fields) => idsMatchSkills(fields.recommended_skill_ids, fields.recommended_skills));

const skillApplicationSchema = z.literal("recommend_non_personal");

export function isSkillDataSourceSelection(value: unknown): value is SkillDataSourceSelection {
  if (!isRecord(value)) return false;
  if (value.mode === "all") {
    return !hasOwn(value, "dataSourceIds") || isStringArray(value.dataSourceIds);
  }
  return value.mode === "custom" && isStringArray(value.dataSourceIds);
}

export function isSkillSummary(value: unknown): value is SkillSummary {
  if (!isRecord(value)) return false;

  return (
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.name) &&
    isNonEmptyString(value.slug) &&
    typeof value.description === "string" &&
    typeof value.enabled === "boolean" &&
    (value.scope === "personal" || value.scope === "team" || value.scope === "org") &&
    (value.source === "manual" || value.source === "directory") &&
    (!hasOwn(value, "origin") ||
      value.origin === "personal" ||
      value.origin === "organization" ||
      value.origin === "finster") &&
    (!hasOwn(value, "category") || isNullableString(value.category)) &&
    hasValidOptionalString(value, "updatedAt") &&
    hasValidOptionalString(value, "companyId", true) &&
    hasValidOptionalString(value, "companyName", true)
  );
}

export function isSkill(value: unknown): value is Skill {
  if (!isRecord(value) || !isSkillSummary(value)) return false;

  return (
    typeof value.instructions === "string" &&
    isSkillDataSourceSelection(value.dataSources) &&
    isStringArray(value.targetFileIds) &&
    (!hasOwn(value, "userFileIds") || isStringArray(value.userFileIds)) &&
    typeof value.createdAt === "string" &&
    typeof value.updatedAt === "string" &&
    hasValidOptionalString(value, "userId") &&
    hasValidOptionalString(value, "orgId", true) &&
    hasValidOptionalString(value, "sourceSkillId")
  );
}

export function isSkillListResponse(value: unknown): value is SkillListResponse {
  return (
    isRecord(value) &&
    Array.isArray(value.items) &&
    value.items.every((item) => isSkillSummary(item))
  );
}

export function isSkillsResolveRequest(value: unknown): value is SkillsResolveRequest {
  if (!isRecord(value)) return false;
  const allowedKeys = new Set([
    "submission_id",
    "messages",
    "skill_ids",
    "skill_application",
    "data_sources",
    "explorer_context",
  ]);
  if (Object.keys(value).some((key) => !allowedKeys.has(key))) return false;
  if (
    !isNonEmptyString(value.submission_id) ||
    !Array.isArray(value.messages) ||
    value.messages.length === 0 ||
    !value.messages.every((message) => isRecord(message))
  ) {
    return false;
  }
  if (hasOwn(value, "skill_ids") && !skillIdListSchema.safeParse(value.skill_ids).success) {
    return false;
  }
  if (
    hasOwn(value, "skill_application") &&
    !skillApplicationSchema.safeParse(value.skill_application).success
  ) {
    return false;
  }
  if (
    hasOwn(value, "data_sources") &&
    (!isStringArray(value.data_sources) || value.data_sources.some((source) => !source.trim()))
  ) {
    return false;
  }
  if (hasOwn(value, "explorer_context")) {
    const context = value.explorer_context;
    if (!isRecord(context)) return false;
    const allowedContextKeys = new Set(["user_file_ids", "user_file_tags"]);
    if (Object.keys(context).some((key) => !allowedContextKeys.has(key))) {
      return false;
    }
    if (hasOwn(context, "user_file_ids") && !isNullableStringArray(context.user_file_ids)) {
      return false;
    }
    if (hasOwn(context, "user_file_tags") && !isNullableStringArray(context.user_file_tags)) {
      return false;
    }
  }
  return true;
}

function hasValidGuidance(guidance: unknown, chars: unknown): boolean {
  if (typeof chars !== "number" || !Number.isInteger(chars) || chars < 0) {
    return false;
  }
  if (guidance === null) {
    return chars === 0;
  }
  if (typeof guidance !== "string") {
    return false;
  }
  const codePointCount = Array.from(guidance).length;
  return codePointCount > 0 && codePointCount === chars;
}

// One current DTO. Zod objects accept and strip unknown response properties.
export const skillsResolveResponseSchema = z
  .object({
    version: z.literal(SKILLS_RESOLVE_VERSION),
    skill_policy_version: z.number().int().min(1),
    submission_id: nonEmptyStringSchema,
    source: z.enum(["explicit", "inferred", "none"]),
    skill_ids: skillIdListSchema,
    skills: z.array(
      skillDecisionSkillSchema.extend({
        data_source_mode: z.enum(["all", "custom"]),
        data_source_ids: z.array(z.string()),
        target_file_ids: z.array(z.string()),
      })
    ),
    guidance: z.string().nullable(),
    guidance_chars: z.number().int().nonnegative(),
    effective_data_sources: z.array(z.string()).nullable(),
    effective_explorer_context: z.object({
      user_file_ids: z.array(z.string()).nullable(),
      user_file_tags: z.array(z.string()).nullable(),
      skill_target_files: z.array(
        z.object({
          id: nonEmptyStringSchema,
          name: nonEmptyStringSchema,
          file_type: nonEmptyStringSchema,
          status: nonEmptyStringSchema,
        })
      ),
    }),
    disposition: z.enum(["applied", "recommended", "none"]).optional(),
    recommended_skill_ids: skillIdListSchema.optional(),
    recommended_skills: z.array(skillDecisionSkillSchema).optional(),
    continuation: z.record(z.string(), z.unknown()).nullish(),
  })
  .refine((response) => {
    if (!hasValidGuidance(response.guidance, response.guidance_chars)) return false;
    if (!idsMatchSkills(response.skill_ids, response.skills)) return false;
    if (new Set(response.skills.map((skill) => skill.slug)).size !== response.skills.length) {
      return false;
    }
    if (response.recommended_skill_ids !== undefined || response.recommended_skills !== undefined) {
      return recommendedFieldsSchema.safeParse(response).success;
    }
    return true;
  });

export function isSkillsResolveResponse(value: unknown): value is SkillsResolveResponse {
  return skillsResolveResponseSchema.safeParse(value).success;
}
